/**
 * Accounts list: a table, because accounts are scanned and compared, not browsed as cards.
 * Built from how the team uses Pipedrive's organizations today: they look up an account by
 * name or FileMaker ID, split clients from prospects, and check who owns it and what's next.
 *
 * - Every column header has a filter; the Columns button shows and hides columns.
 * - Values with a pencil on hover are edited in place (EditableCell); a lock means the API
 *   can't save that field yet and it's edited in zInspector.
 * - "Views" holds ready-made and saved views; Export downloads the filtered list with each
 *   account's main contact, for campaigns.
 */
import { useQuery } from "@tanstack/react-query";
import { AlertCircle, Mail, Phone } from "lucide-react";
import {
  useDataProvider,
  useGetIdentity,
  useGetList,
  useTranslate,
} from "ra-core";
import { useMemo } from "react";

import { ColumnsButton } from "@/components/admin/columns-button";
import { CreateButton } from "@/components/admin/create-button";
import { DataTable } from "@/components/admin/data-table";
import { ExportButton } from "@/components/admin/export-button";
import { List } from "@/components/admin/list";
import { ListPagination } from "@/components/admin/list-pagination";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

import { TopToolbar } from "../layout/TopToolbar";
import type { ZcrmAccount } from "../providers/zinspector/accounts";
import {
  ACCOUNT_KINDS,
  ACCOUNT_STATUSES,
  HEALTH_SEGMENTS,
} from "../providers/zinspector/crmConfig";
import type { ZcrmDataProvider } from "../providers/zinspector/dataProvider";
import type { Sale } from "../types";
import { AccountDuplicates } from "./AccountDuplicates";
import { accountExporter } from "./accountExporter";
import { AccountListFilters, AccountViewTabs } from "./AccountListFilters";
import { AccountRowActions } from "./AccountRowActions";
import { AccountViewsMenu } from "./AccountViewsMenu";
import { ColumnFilter, type ColumnFilterSpec } from "./ColumnFilter";
import { EditableCell, type EditorSpec } from "./EditableCell";
import {
  AccountHealth,
  AccountKind,
  AccountStatus,
  EmptyValue,
  formatDoors,
  LastTouch,
  NextStep,
} from "./accountDisplay";

const PER_PAGE = 50;
const ALL = { page: 1, perPage: 10_000 };
/** Hidden until someone turns them on from "Columns". */
const HIDDEN_BY_DEFAULT = ["health", "lead_source", "pm_software"];
const DAY_MS = 24 * 60 * 60 * 1000;

export const AccountList = () => (
  <List
    title={false}
    perPage={PER_PAGE}
    sort={{ field: "name", order: "ASC" }}
    filterDefaultValues={{ view: "clients" }}
    actions={<AccountListActions />}
    exporter={accountExporter}
    pagination={<ListPagination rowsPerPageOptions={[25, 50, 100]} />}
  >
    <AccountListLayout />
  </List>
);

const AccountListActions = () => {
  const translate = useTranslate();
  return (
    <TopToolbar>
      <AccountViewsMenu />
      <ColumnsButton />
      <AccountDuplicates />
      <ExportButton />
      <CreateButton
        label={translate("resources.companies.action.new", {
          _: "New account",
        })}
      />
    </TopToolbar>
  );
};

const AccountListLayout = () => {
  const { identity } = useGetIdentity();
  return (
    // min-w-0 lets the table scroll sideways instead of widening the page.
    <div className="flex flex-col gap-3 min-w-0">
      <AccountViewTabs />
      <AccountListFilters identityId={identity?.id} />
      <AccountTable />
    </div>
  );
};

const daysAgo = (days: number) =>
  new Date(Date.now() - days * DAY_MS).toISOString().slice(0, 10);

/** The values each filter and editor offers, from the data the provider has already cached. */
const useTableOptions = () => {
  const dataProvider = useDataProvider<ZcrmDataProvider>();
  const { data: accounts = [] } = useGetList<ZcrmAccount>("companies", {
    pagination: ALL,
    sort: { field: "name", order: "ASC" },
  });
  const { data: sales = [] } = useGetList<Sale>("sales", {
    pagination: ALL,
    sort: { field: "first_name", order: "ASC" },
  });
  const { data: groups = [] } = useQuery({
    queryKey: ["zcrm", "markets"],
    queryFn: () => dataProvider.getMarkets(),
  });
  return useMemo(() => {
    const distinct = (values: (string | null | undefined)[]) =>
      [...new Set(values.filter((v): v is string => !!v))]
        .sort()
        .map((value) => ({ value, label: value }));
    return {
      markets: distinct(accounts.map((a) => a.market)),
      marketGroups: groups,
      people: sales.map((s) => ({
        value: String(s.id),
        label: `${s.first_name} ${s.last_name}`.trim(),
      })),
      kinds: ACCOUNT_KINDS.map((k) => ({ value: k, label: k })),
      health: HEALTH_SEGMENTS.map((h) => ({ value: h.value, label: h.value })),
    };
  }, [accounts, sales, groups]);
};

const AccountTable = () => {
  const translate = useTranslate();
  const options = useTableOptions();
  const fieldLabel = (field: string) =>
    translate(`resources.companies.fields.${field}`);

  const filter = (field: string, spec: ColumnFilterSpec) => (
    <ColumnFilter label={fieldLabel(field)} spec={spec} />
  );
  const editable = (
    account: ZcrmAccount,
    field: string,
    spec: EditorSpec,
    display: React.ReactNode,
  ) => (
    <EditableCell account={account} label={fieldLabel(field)} spec={spec}>
      {display}
    </EditableCell>
  );

  return (
    <DataTable<ZcrmAccount>
      rowClick="show"
      bulkActionButtons={false}
      hiddenColumns={HIDDEN_BY_DEFAULT}
    >
      <DataTable.Col<ZcrmAccount>
        source="name"
        filter={filter("name", { type: "text", field: "name" })}
        render={(account) => <AccountNameCell account={account} />}
      />
      <DataTable.Col<ZcrmAccount>
        source="account_kind"
        filter={filter("account_kind", {
          type: "choice",
          field: "account_kind",
          options: options.kinds,
        })}
        render={(account) =>
          editable(
            account,
            "account_kind",
            { type: "select", field: "account_kind", options: options.kinds },
            account.account_kind ? (
              <AccountKind value={account.account_kind} />
            ) : (
              <EmptyValue />
            ),
          )
        }
      />
      <DataTable.Col<ZcrmAccount>
        source="status"
        filter={filter("status", {
          type: "choice",
          field: "status",
          options: ACCOUNT_STATUSES.map((s) => ({
            value: s.id,
            label: s.label,
          })),
        })}
        render={(account) =>
          editable(
            account,
            "status",
            { type: "status" },
            <AccountStatus account={account} />,
          )
        }
      />
      <DataTable.Col<ZcrmAccount>
        source="health"
        filter={filter("health", {
          type: "choice",
          field: "health",
          options: options.health,
        })}
        render={(account) =>
          editable(
            account,
            "health",
            { type: "select", field: "health", options: options.health },
            <AccountHealth value={account.health} />,
          )
        }
      />
      <DataTable.Col<ZcrmAccount>
        source="market"
        filter={filter("market", {
          type: "choice",
          field: "market",
          options: options.markets,
        })}
        render={(account) =>
          editable(
            account,
            "market",
            {
              type: "select",
              field: "user_group",
              options: options.marketGroups,
            },
            account.market || <EmptyValue />,
          )
        }
      />
      <DataTable.Col<ZcrmAccount>
        source="doors_total"
        label="resources.companies.fields.doors"
        sortByOrder="DESC"
        filter={filter("doors", { type: "range", field: "doors_total" })}
        render={(account) =>
          editable(
            account,
            "doors",
            { type: "doors" },
            <DoorsCell account={account} />,
          )
        }
      />
      <DataTable.Col<ZcrmAccount>
        source="account_manager_id"
        filter={filter("account_manager_id", {
          type: "choice",
          field: "account_manager_id",
          options: options.people,
        })}
        render={(account) =>
          editable(
            account,
            "account_manager_id",
            {
              type: "select",
              field: "account_manager_id",
              options: options.people,
            },
            options.people.find(
              (p) => p.value === String(account.account_manager_id),
            )?.label ?? <EmptyValue />,
          )
        }
      />
      <DataTable.Col<ZcrmAccount>
        source="main_contact_name"
        filter={filter("main_contact_name", {
          type: "text",
          field: "main_contact_name",
        })}
        render={(account) => <MainContactCell account={account} />}
      />
      <DataTable.Col<ZcrmAccount>
        source="accounting_id"
        filter={filter("accounting_id", {
          type: "preset",
          options: [
            { id: "missing", label: "Missing", filter: { accounting_id: "" } },
          ],
        })}
        render={(account) =>
          editable(
            account,
            "accounting_id",
            { type: "text", field: "accounting_id" },
            account.accounting_id || <EmptyValue />,
          )
        }
      />
      <DataTable.Col<ZcrmAccount>
        source="next_step_due"
        label="resources.companies.fields.next_step"
        cellClassName="max-w-56"
        filter={filter("next_step", {
          type: "preset",
          options: [
            {
              id: "overdue",
              label: "Overdue",
              filter: { "next_step_due@lt": daysAgo(0) },
            },
            { id: "none", label: "No next step", filter: { next_step: "" } },
          ],
        })}
        render={(account) =>
          editable(
            account,
            "next_step",
            { type: "nextStep" },
            <NextStep account={account} />,
          )
        }
      />
      <DataTable.Col<ZcrmAccount>
        source="last_touch"
        sortByOrder="DESC"
        filter={filter("last_touch", {
          type: "preset",
          options: [
            {
              id: "7d",
              label: "In the last 7 days",
              filter: { "last_touch@gte": daysAgo(7) },
            },
            {
              id: "30d",
              label: "In the last 30 days",
              filter: { "last_touch@gte": daysAgo(30) },
            },
            {
              id: "stale30",
              label: "Not in 30 days",
              filter: { "last_touch@lte": daysAgo(30) },
            },
            {
              id: "stale90",
              label: "Not in 90 days",
              filter: { "last_touch@lte": daysAgo(90) },
            },
          ],
        })}
        render={(account) => <LastTouch date={account.last_touch} />}
      />
      <DataTable.Col<ZcrmAccount>
        source="open_deals"
        label="resources.companies.fields.open_work"
        sortByOrder="DESC"
        filter={filter("open_work", {
          type: "preset",
          options: [
            {
              id: "deals",
              label: "Has open deals",
              filter: { "open_deals@gt": 0 },
            },
            {
              id: "tickets",
              label: "Has open tickets",
              filter: { "open_tickets@gt": 0 },
            },
          ],
        })}
        render={(account) => <OpenWork account={account} />}
      />
      <DataTable.Col<ZcrmAccount>
        source="lead_source"
        filter={filter("lead_source", { type: "text", field: "lead_source" })}
        render={(account) =>
          editable(
            account,
            "lead_source",
            { type: "text", field: "lead_source" },
            account.lead_source || <EmptyValue />,
          )
        }
      />
      <DataTable.Col<ZcrmAccount>
        source="pm_software"
        filter={filter("pm_software", { type: "text", field: "pm_software" })}
        render={(account) =>
          editable(
            account,
            "pm_software",
            { type: "text", field: "pm_software" },
            account.pm_software || <EmptyValue />,
          )
        }
      />
      <DataTable.Col<ZcrmAccount>
        source="id"
        label=" "
        disableSort
        render={(account) => <AccountRowActions account={account} />}
      />
    </DataTable>
  );
};

const AccountNameCell = ({ account }: { account: ZcrmAccount }) => (
  <span className="flex flex-col gap-0.5 min-w-0">
    <span className="flex items-center gap-1.5">
      <span className="font-medium truncate">{account.name}</span>
      {account.needs_info ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <AlertCircle
              className="size-3.5 shrink-0 text-warning"
              aria-label={`Missing: ${account.missing_info.join(", ")}`}
            />
          </TooltipTrigger>
          <TooltipContent>
            Missing: {account.missing_info.join(", ")}
          </TooltipContent>
        </Tooltip>
      ) : null}
    </span>
    <span className="text-xs text-muted-foreground truncate">
      {[account.city, account.state_abbr].filter(Boolean).join(", ")}
    </span>
  </span>
);

const DoorsCell = ({ account }: { account: ZcrmAccount }) => {
  const doors = formatDoors(account);
  if (!doors) return <EmptyValue />;
  return (
    <span className="flex flex-col tabular-nums">
      <span>{doors}</span>
      {account.doors_share !== null ? (
        <span className="text-xs text-muted-foreground">
          {account.doors_share}% served
        </span>
      ) : null}
    </span>
  );
};

/** The main contact, with one-click email and call. */
const MainContactCell = ({ account }: { account: ZcrmAccount }) => {
  if (!account.main_contact_name) return <EmptyValue />;
  const stop = (event: React.MouseEvent) => event.stopPropagation();
  return (
    <span className="flex items-center gap-2 min-w-0">
      <span className="truncate">{account.main_contact_name}</span>
      {account.main_contact_email ? (
        <a
          href={`mailto:${account.main_contact_email}`}
          onClick={stop}
          aria-label={`Email ${account.main_contact_name}`}
          title={account.main_contact_email}
          className="text-muted-foreground hover:text-foreground"
        >
          <Mail className="size-3.5" />
        </a>
      ) : null}
      {account.main_contact_phone ? (
        <a
          href={`tel:${account.main_contact_phone}`}
          onClick={stop}
          aria-label={`Call ${account.main_contact_name}`}
          title={account.main_contact_phone}
          className="text-muted-foreground hover:text-foreground"
        >
          <Phone className="size-3.5" />
        </a>
      ) : null}
    </span>
  );
};

/** "2 · 1": open deals, then open tickets, each with its own tooltip. */
const OpenWork = ({ account }: { account: ZcrmAccount }) => {
  const translate = useTranslate();
  if (!account.open_deals && !account.open_tickets) return <EmptyValue />;
  return (
    <span className="tabular-nums">
      <span title={translate("resources.companies.fields.open_deals")}>
        {account.open_deals}
      </span>
      <span className="text-muted-foreground"> · </span>
      <span title={translate("resources.companies.fields.open_tickets")}>
        {account.open_tickets}
      </span>
    </span>
  );
};
